import { z } from "zod";
import Switch from "../../../../ui/Switch/Switch";
import SettingsSection from "../../components/SettingsSection";
import Header from "../../components/Header/Header";
import styles from "./ContentOptions.module.scss";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import { useToast } from "../../../../ui/Toast/ToastContext";
import { graphql } from "../../../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import { useMemo } from "react";
import Loader from "../../../../ui/Loader/Loader";
import { Case } from "../../../../__generated__/gql/graphql";
import Label from "../../../../ui/Label/Label";
import Select from "../../../../ui/Select/Select";
import Tooltip from "../../../../ui/Tooltip/Tooltip";
import { Help } from "../../../../ui/Icon/Icon";
import useFeatureFlag from "../../../../providers/FeatureFlagProvider/useFeatureFlag";

const ContentSettingsQuery = graphql(/* GraphQL */ `
  query contentSettings {
    contentSettings {
      generateTableOfContents
      articleTitleCase
      headingCase
      metaDescriptionCase
      copyArticleTitle
      copyExcerpt
      copyHeroImage
      copyMetaData
      language
      sectionWritingVersion
    }
  }
`);

const UpdateContentSettingsMutation = graphql(/* GraphQL */ `
  mutation updateContentSettings($input: UpdateContentSettingsInput!) {
    updateContentSettings(input: $input) {
      generateTableOfContents
      articleTitleCase
      headingCase
      metaDescriptionCase
      copyArticleTitle
      copyExcerpt
      copyHeroImage
      copyMetaData
      language
      sectionWritingVersion
    }
  }
`);

const cases = z.enum([
  Case.SentenceCase,
  Case.TitleCase,
  Case.UpperCase,
  Case.StartCase,
]);

const schema = z.object({
  generateTableOfContents: z.boolean(),
  articleTitleCase: cases,
  headingCase: cases,
  metaDescriptionCase: cases,
  copyArticleTitle: z.boolean(),
  copyExcerpt: z.boolean(),
  copyHeroImage: z.boolean(),
  copyMetaData: z.boolean(),
  language: z.string(),
  sectionWritingVersion: z.number(),
  // biasYourBrand: z.boolean(),
});

type ContentOptionsFormData = z.infer<typeof schema>;

const caseOptions = [
  {
    label: "Title case",
    sublabel: "An Example of the Title Case Format",
    value: Case.TitleCase,
  },
  {
    label: "Start case",
    sublabel: "An Example Of The Start Case Format",
    value: Case.StartCase,
  },
  {
    label: "Sentence case",
    sublabel: "An example of the sentence case format",
    value: Case.SentenceCase,
  },
  {
    label: "All caps",
    sublabel: "AN EXAMPLE OF THE ALL CAPS FORMAT",
    value: Case.UpperCase,
  },
];

const languages = [
  {
    label: "English (US)",
    value: "english-us",
  },
  {
    label: "English (UK)",
    value: "english-uk",
  },
  {
    label: "Spanish",
    value: "spanish",
  },
  {
    label: "German",
    value: "german",
  },
  {
    label: "French",
    value: "french",
  },
  {
    label: "Italian",
    value: "italian",
  },
  {
    label: "Portuguese",
    value: "portuguese",
  },
  {
    label: "Dutch",
    value: "dutch",
  },
  {
    label: "Norwegian",
    value: "norwegian",
  },
  {
    label: "Swedish",
    value: "swedish",
  },
  {
    label: "Finnish",
    value: "finnish",
  },
  {
    label: "Danish",
    value: "danish",
  },
  {
    label: "Hungarian",
    value: "hungarian",
  },
  {
    label: "Estonian",
    value: "estonian",
  },
  {
    label: "Croatian",
    value: "croatian",
  },
  {
    label: "Ukrainian",
    value: "ukrainian",
  },
  {
    label: "Russian",
    value: "russian",
  },
  {
    label: "Greek",
    value: "greek",
  },
  {
    label: "Polish",
    value: "polish",
  },
  {
    label: "Turkish",
    value: "turkish",
  },
  {
    label: "Romanian",
    value: "romanian",
  },
  {
    label: "Hebrew",
    value: "hebrew",
  },
];

function ContentOptionsForm({
  defaultValues,
  onSubmit,
}: {
  defaultValues: ContentOptionsFormData;
  onSubmit: (data: ContentOptionsFormData) => void;
}) {
  const { featureFlags } = useFeatureFlag();
  const { showToast } = useToast();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<ContentOptionsFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmitWrapper = (data: ContentOptionsFormData) => {
    onSubmit(data);

    reset(data);
    showToast({
      title: "Content option settings saved",
      variant: "success",
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmitWrapper)}>
      <UnsavedChangesModal isDirty={isDirty} />
      <Header
        title="Content Options"
        description="Extra controls over optional generation settings"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        onCancel={() => reset()}
      />
      {/* <SettingsSection
        label="Generation"
        description="Configurable options for article generation"
      >
        <div className={styles.switches}>
          <Controller
            name="generateTableOfContents"
            control={control}
            render={({ field }) => (
              <label className={styles.switchContainer} htmlFor={field.name}>
                <Switch
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className={styles.switchLabel}>
                  Generate table of contents
                </span>
              </label>
            )}
          />
          <Controller
            name="biasYourBrand"
            control={control}
            render={({ field }) => (
              <label className={styles.switchContainer} htmlFor={field.name}>
                <Switch
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className={styles.switchLabel}>Bias your brand </span>
              </label>
            )}
          />
        </div>
      </SettingsSection> */}

      <SettingsSection
        label="Default language"
        description="Choose what language Macaw will write articles in"
      >
        <div className={styles.inputSection}>
          <Label label="Language">
            <Controller
              name="language"
              control={control}
              render={({ field }) => <Select data={languages} {...field} />}
            />
          </Label>
        </div>
      </SettingsSection>

      <SettingsSection
        label="Title format"
        description="Choose how titles should be formatted in articles"
      >
        <div className={styles.inputSection}>
          <Label label="Article title">
            <Controller
              name="articleTitleCase"
              control={control}
              render={({ field }) => <Select data={caseOptions} {...field} />}
            />
          </Label>
          <Label label="Heading titles">
            <Controller
              name="headingCase"
              control={control}
              render={({ field }) => <Select data={caseOptions} {...field} />}
            />
          </Label>
          <Label label="Meta description">
            <Controller
              name="metaDescriptionCase"
              control={control}
              render={({ field }) => <Select data={caseOptions} {...field} />}
            />
          </Label>
        </div>
      </SettingsSection>

      <SettingsSection
        label="Article copy elements"
        description="Choose what information is included in the article copy action"
      >
        <div className={styles.switches}>
          <label className={styles.switchContainer} htmlFor="copyArticleBody">
            <Switch id="copyArticleBody" checked={true} disabled />
            <span className={styles.switchLabel}>Article body</span>
          </label>
          <Controller
            name="copyArticleTitle"
            control={control}
            render={({ field }) => (
              <label className={styles.switchContainer} htmlFor={field.name}>
                <Switch
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className={styles.switchLabel}>Article title</span>
              </label>
            )}
          />
          <Controller
            name="copyExcerpt"
            control={control}
            render={({ field }) => (
              <label className={styles.switchContainer} htmlFor={field.name}>
                <Switch
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className={styles.switchLabel}>Excerpt</span>
              </label>
            )}
          />
          <Controller
            name="copyHeroImage"
            control={control}
            render={({ field }) => (
              <label className={styles.switchContainer} htmlFor={field.name}>
                <Switch
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className={styles.switchLabel}>Hero image</span>
              </label>
            )}
          />
          <Controller
            name="copyMetaData"
            control={control}
            render={({ field }) => (
              <label className={styles.switchContainer} htmlFor={field.name}>
                <Switch
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className={styles.switchLabel}>Meta data</span>
                <Tooltip content="This will add a section below the article content that includes the meta description, keyword and tag.">
                  <Help className={styles.tooltip} />
                </Tooltip>
              </label>
            )}
          />
        </div>
      </SettingsSection>
      {featureFlags.sectionWritingVersionSelector && (
        <SettingsSection
          label="Section writing version"
          description="Choose what version of our section writing prompts are used when creating your articles"
        >
          <div className={styles.inputSection}>
            <Label label="Version">
              <Controller
                name="sectionWritingVersion"
                control={control}
                render={({ field }) => (
                  <Select
                    data={[
                      {
                        value: "1",
                        label: "v1",
                      },
                      { value: "2", label: "v2" },
                    ]}
                    {...field}
                    value={field.value.toString()}
                    onChange={(value) => {
                      field.onChange(value ? parseInt(value) : 2);
                    }}
                  />
                )}
              />
            </Label>
          </div>
        </SettingsSection>
      )}
    </form>
  );
}

function ContentOptions() {
  const [contentSettingsResponse] = useQuery({ query: ContentSettingsQuery });
  const [, updateContentSettings] = useMutation(UpdateContentSettingsMutation);

  const contentSettings = contentSettingsResponse.data?.contentSettings;

  const onSubmit = async (data: ContentOptionsFormData) => {
    await updateContentSettings({ input: data });
  };

  const defaultValues: ContentOptionsFormData = useMemo(() => {
    if (!contentSettings) {
      return {
        generateTableOfContents: false,
        articleTitleCase: Case.TitleCase,
        headingCase: Case.TitleCase,
        metaDescriptionCase: Case.SentenceCase,
        copyArticleTitle: false,
        copyExcerpt: false,
        copyHeroImage: false,
        copyMetaData: false,
        language: "english-us",
        sectionWritingVersion: 2,
        // biasYourBrand: false,
      };
    }

    return {
      generateTableOfContents: !!contentSettings.generateTableOfContents,
      articleTitleCase: contentSettings.articleTitleCase,
      headingCase: contentSettings.headingCase,
      metaDescriptionCase: contentSettings.metaDescriptionCase,
      copyArticleTitle: !!contentSettings.copyArticleTitle,
      copyExcerpt: !!contentSettings.copyExcerpt,
      copyHeroImage: !!contentSettings.copyHeroImage,
      copyMetaData: !!contentSettings.copyMetaData,
      language: contentSettings.language ?? "english-us",
      sectionWritingVersion: contentSettings.sectionWritingVersion,
      // biasYourBrand: false,
    };
  }, [contentSettings]);

  if (contentSettingsResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <ContentOptionsForm onSubmit={onSubmit} defaultValues={defaultValues} />
  );
}

export default ContentOptions;
